import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/config";

export interface PricingPlan {
  id: string;
  name: string;
  price: string;
  capacity: string;
  includesText: string;
  features: string[];
  badge?: string;
  cta: string;
  ctaStyle: "primary" | "muted";
  isCurrent?: boolean;
}

interface PricingConfig {
  heading: string;
  subheading: string;
  plans: PricingPlan[];
}

const defaultPlans: PricingPlan[] = [
  {
    id: "free",
    name: "Free",
    badge: "CURRENT",
    price: "$0",
    capacity: "Up to 50 students",
    includesText: "Perfect for small schools:",
    features: ["Basic attendance tracking", "1 admin account", "Email support", "Core reporting features"],
    cta: "Current plan",
    ctaStyle: "muted",
    isCurrent: true
  },
  {
    id: "starter",
    name: "Starter",
    badge: "",
    price: "$19",
    capacity: "Up to 200 students",
    includesText: "Everything in Free, plus:",
    features: ["5 staff accounts", "Basic analytics dashboard", "SMS notifications", "Priority email support"],
    cta: "Upgrade to Starter",
    ctaStyle: "primary",
    isCurrent: false
  },
  {
    id: "pro",
    name: "Professional",
    badge: "POPULAR",
    price: "$49",
    capacity: "Up to 1,000 students",
    includesText: "Everything in Starter, plus:",
    features: ["Unlimited staff accounts", "Advanced analytics", "Priority phone support", "API access"],
    cta: "Upgrade to Professional",
    ctaStyle: "primary",
    isCurrent: false
  },
  {
    id: "enterprise",
    name: "Enterprise",
    badge: "",
    price: "$99",
    capacity: "Unlimited students",
    includesText: "Everything in Professional, plus:",
    features: ["Dedicated success manager", "Custom integrations", "99.9% SLA guarantee", "On-premise deployment"],
    cta: "Contact Sales",
    ctaStyle: "primary",
    isCurrent: false
  },
];

const Pricing = () => {
    const [config, setConfig] = useState<PricingConfig>({
        heading: "Simple, transparent pricing",
        subheading: "Choose the plan that fits your school's needs. No hidden fees.",
        plans: defaultPlans
    });

    useEffect(() => {
        const fetchPricing = async () => {
            try {
                const docRef = doc(db, "systemConfig/pricing");
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data() as Partial<PricingConfig>;
                    setConfig(prev => ({
                        ...prev,
                        ...data,
                        plans: data.plans || prev.plans
                    }));
                }
            } catch (error) {
                console.error("Error fetching pricing:", error);
            }
        };
        fetchPricing();
    }, []);

  return (
    <section id="pricing" className="py-24 lg:py-32 px-6 lg:px-8 bg-slate-50/50 dark:bg-slate-900/50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="max-w-2xl mb-16 mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            viewport={{ once: true, margin: "-50px" }}
            className="text-3xl sm:text-4xl font-bold tracking-tight mb-4 text-slate-900 dark:text-slate-50"
          >
            {config.heading}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            viewport={{ once: true, margin: "-50px" }}
            className="text-lg text-slate-600 dark:text-slate-400"
          >
            {config.subheading}
          </motion.p>
        </div>
        
        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {config.plans.map((plan, index) => (
            <motion.div
              key={plan.id || index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: "easeOut" }}
              viewport={{ once: true, margin: "-50px" }}
              className={`relative flex flex-col h-full rounded-2xl p-8 transition-all duration-300 ${
                plan.isCurrent 
                  ? 'bg-white dark:bg-[#020817] ring-2 ring-blue-600 dark:ring-blue-500 shadow-xl shadow-blue-900/5' 
                  : 'bg-white dark:bg-[#020817] border border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-lg'
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-0 right-0 flex justify-center">
                  <span className="px-3 py-1 text-[10px] font-bold tracking-wide uppercase bg-blue-600 text-white rounded-full shadow-sm">
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-slate-900 dark:text-slate-50">{plan.price}</span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">/month</span>
                </div>
              </div>
              
              {/* Capacity Badge */}
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-full px-3 py-2 text-xs font-medium bg-slate-100 dark:bg-slate-800/80 text-slate-700 dark:text-slate-300 rounded-lg border border-slate-200 dark:border-slate-700">
                  {plan.capacity}
                </div>
              </div>
              
              {/* Features List */}
              <div className="flex-grow">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">{plan.includesText}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300">
                      <div className="mt-0.5 w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                        <Check className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="leading-tight">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* CTA Button */}
              <Button 
                className={`w-full rounded-lg font-medium h-11 transition-all ${
                  plan.ctaStyle === 'muted' 
                    ? 'bg-slate-100 text-slate-900 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-50 dark:hover:bg-slate-700' 
                    : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:text-white dark:hover:bg-blue-500 shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30'
                }`}
                variant={plan.ctaStyle === 'muted' ? "secondary" : "default"}
                data-testid={`button-pricing-${index}`}
              >
                {plan.cta}
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
